import { useApolloClient } from '@apollo/client/react'
import { Box, Button, Flex, HStack, Spacer } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft } from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { useProfileActivation } from '@/auth/useProfileActivation'
import { Logo } from '@/components/atoms/Logo/Logo'
import { NewCircleWizard } from '@/components/molecules/Form/Circles/CircleForm/newCircle/NewCircleWizard'
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog'
import {
  type ProfileKey,
  PROFILES,
} from '@/components/views/accountManagement/registerWizard/steps/congratulations/profiles/profilesConfig'
import { NetworkNewWizard } from '@/components/views/networks/new/NetworkNewWizard'
import { AuthUserDocument } from '@/graphql/api/generated'
import { useTracking } from '@/hooks/useTracking'
import { CommunitiesRoutes, PoolingRoutes } from '@/routes/_hooks/routes.enum'
import { useNavigate } from '@/routing'

import { ProfileIntroScreen } from './ProfileIntroScreen'
import { ProfileSelectionScreen } from './ProfileSelectionScreen'
import { MemberSubflow } from './subflows/MemberSubflow'

type OnboardingPhase = 'select' | 'intro' | 'subflow'

interface ProfileOnboardingDialogProps {
  /**
   * `false` (default): forced onboarding — no close affordance, the user must
   * activate a profile (or, for "member", pass through the skippable sub-flow).
   * `true`: reused later to let an existing user add another profile.
   */
  closable?: boolean
  /** Closes the dialog when `closable`. */
  onClose?: () => void
}

/**
 * Full-screen profile orientation experience: pick a profile → read its intro →
 * run its sub-flow. Mounted non-closeable by the global gate for brand-new
 * users, and closeable when reopened to add an extra profile.
 */
export const ProfileOnboardingDialog = ({
  closable = false,
  onClose,
}: ProfileOnboardingDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const auth = useAuth()
  const apolloClient = useApolloClient()
  const { captureEvent } = useTracking()
  const { markSkipped } = useProfileActivation()

  const [phase, setPhase] = useState<OnboardingPhase>('select')
  const [selectedKey, setSelectedKey] = useState<ProfileKey | null>(null)
  const selected =
    PROFILES.find((profile) => profile.key === selectedKey) ?? null

  const handleSelect = (key: ProfileKey) => {
    captureEvent('registration_profile_selected', {
      profile: key,
      user_id: auth.user?.id,
    })
    setSelectedKey(key)
    setPhase('intro')
  }

  const handleStart = () => {
    if (!selected) {
      return
    }
    captureEvent('registration_profile_started', { profile: selected.key })
    setPhase('subflow')
  }

  const backToSelection = () => {
    setSelectedKey(null)
    setPhase('select')
  }

  // Member sub-flow exit: nothing is guaranteed activated, so persist the skip
  // to release the gate, then either close (reopened) or land in the app.
  const finishMember = () => {
    markSkipped()
    if (closable) {
      onClose?.()
    } else {
      navigate(PoolingRoutes.Search)
    }
  }

  // Animator: NewCircleWizard's createCircle does not refresh currentUser, so
  // refetch (gate then sees the new Owner membership) and skip as a race-proof
  // belt, then leave the dialog.
  const finishAnimator = () => {
    void apolloClient.refetchQueries({ include: [AuthUserDocument] })
    markSkipped()
    if (closable) {
      onClose?.()
    } else {
      navigate(CommunitiesRoutes.MyCommunities)
    }
  }

  // Federator: NetworkNewWizard switches context (which refetches currentUser)
  // and navigates itself. Skip as the same race-proof belt as animator, and
  // close the dialog when reused in closable mode.
  const finishFederator = () => {
    markSkipped()
    if (closable) {
      onClose?.()
    }
  }

  return (
    <DialogRoot
      open
      size="full"
      closeOnEscape={closable}
      closeOnInteractOutside={closable}
      onOpenChange={(event) => {
        if (!event.open) {
          onClose?.()
        }
      }}
    >
      <DialogContent
        w="100dvw"
        h="100dvh"
        maxW="unset"
        maxH="100dvh"
        borderRadius="0"
        p="0"
        display="flex"
        flexDirection="column"
        bg="bg.panel"
        color="fg"
      >
        <HStack
          px={{ base: '4', md: '8' }}
          py="3"
          borderBottomWidth="1px"
          borderColor="border"
          position="sticky"
          top="0"
          bg="bg.panel"
          zIndex="1"
          gap="3"
        >
          <Box flexShrink={0}>
            <Logo width="140px" />
          </Box>
          {phase !== 'select' && (
            <Button variant="ghost" size="sm" onClick={backToSelection}>
              <LuArrowLeft />
              {t('register.wizard.profiles.chooseAnother')}
            </Button>
          )}
          <Spacer />
          {closable && <DialogCloseTrigger position="static" />}
        </HStack>

        <DialogBody
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          px={{ base: '4', md: '8' }}
          py={{ base: '4', md: '6' }}
        >
          <Flex direction="column" minH="full">
            {phase === 'select' && (
              <ProfileSelectionScreen onSelect={handleSelect} />
            )}

            {phase === 'intro' && selected && (
              <ProfileIntroScreen profile={selected} onStart={handleStart} />
            )}

            {phase === 'subflow' && selected?.key === 'member' && (
              <MemberSubflow onComplete={finishMember} />
            )}

            {phase === 'subflow' && selected?.key === 'animator' && (
              <Box w="full">
                {/* Embedded: onSuccess refetches + leaves the dialog. */}
                <NewCircleWizard
                  variant="embedded"
                  onSuccess={finishAnimator}
                />
              </Box>
            )}

            {phase === 'subflow' && selected?.key === 'federator' && (
              <Box w="full">
                {/* Completes by switching context + navigating to the network. */}
                <NetworkNewWizard onCompleted={finishFederator} />
              </Box>
            )}
          </Flex>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  )
}
